import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Share2, 
  Clock, 
  Calendar, 
  Headphones,
  Edit3,
  Trash2,
  Plus
} from 'lucide-react';
import type { PodcastWithRelations } from '../../../hooks/usePodcasts';
import { Link } from 'react-router-dom';
import { PlayButton } from './PlayButton';
import { Pagination } from '../../../components/shared/Pagination';
import { scrollToSection } from '../../../utils/scroll';
import { useAuth } from '../../../hooks/useAuth';

const ITEMS_PER_PAGE = 6;

interface PodcastListProps {
  podcasts: PodcastWithRelations[];
  category?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  isAdminMode: boolean;
  currentPodcast: PodcastWithRelations | null;
  isPlaying: boolean;
  loadingAudio: boolean;
  onPlayPause: (podcast: PodcastWithRelations) => void;
  onEdit: (podcast: PodcastWithRelations) => void;
  onDelete: (id: string) => void;
  onCreateNew: () => void;
}

export const PodcastList: React.FC<PodcastListProps> = ({
  podcasts,
  category,
  currentPage,
  onPageChange,
  isAdminMode,
  currentPodcast,
  isPlaying,
  loadingAudio,
  onPlayPause,
  onEdit,
  onDelete,
  onCreateNew
}) => {
  const { user } = useAuth();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Only show admin features if user is logged in AND admin mode is enabled
  const showAdminFeatures = isAdminMode && !!user;

  const filteredPodcasts = category
    ? podcasts
        .filter(podcast => {
          const categoryObj = podcast.category;
          return categoryObj?.slug === category;
        })
        .sort((a, b) => new Date(b.published_at || '').getTime() - new Date(a.published_at || '').getTime())
    : podcasts.sort((a, b) => new Date(b.published_at || '').getTime() - new Date(a.published_at || '').getTime());

  const totalPages = Math.ceil(filteredPodcasts.length / ITEMS_PER_PAGE);
  const paginatedPodcasts = filteredPodcasts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleShare = (podcast: PodcastWithRelations) => {
    if (navigator.share) {
      navigator.share({
        title: podcast.title,
        text: podcast.description || '',
        url: window.location.href
      });
    }
  };

  const handlePodcastClick = (e: React.MouseEvent, podcast: PodcastWithRelations) => {
    const target = e.target as HTMLElement;
    // Se o clique foi em um botão ou dentro de um botão, não navega
    if (target.closest('button')) {
      e.preventDefault();
    }
  };

  const handlePageChange = (page: number) => {
    onPageChange(page);
    scrollToSection('podcast-list');
  };

  return (
    <motion.div
      ref={ref}
      id="podcast-list"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      {showAdminFeatures && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-lg p-6 border-2 border-dashed border-primary/30 hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-4 text-center"
          onClick={onCreateNew}
        >
          <Plus className="w-12 h-12 text-primary" />
          <p className="text-lg font-semibold">Adicionar Novo Podcast</p>
          <p className="text-sm text-gray-400">Clique para criar</p>
        </motion.div>
      )}

      {paginatedPodcasts.map((podcast) => (
        <motion.div
          key={podcast.id}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          whileHover={{ scale: 1.02 }}
          transition={{ 
            duration: 0.2,
            scale: {
              type: "spring",
              stiffness: 300,
              damping: 20
            }
          }}
          className="group bg-card rounded-lg overflow-hidden hover:ring-1 hover:ring-primary/50 transition-all relative"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            whileHover={{ scale: 1.02 }}
            transition={{ 
              duration: 0.2,
              scale: {
                type: "spring",
                stiffness: 300,
                damping: 20
              }
            }}
            className="relative"
          >
            {/* Imagem de capa */}
            <div className="relative h-48 overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${podcast.image_url})` }}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.4 }}
              />
              <motion.div
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent"
              />
              <Link
                to={`/podcasts/${podcast.id}`}
                className="absolute inset-0 z-10"
                onClick={(e) => {
                  // Prevent navigation if clicking on a button
                  if ((e.target as HTMLElement).closest('button')) {
                    e.preventDefault();
                  }
                }}
              />
            </div>

            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <span className="px-3 py-1 bg-secondary rounded-full text-sm">
                  {podcast.category?.name}
                </span>
                <div className="flex items-center gap-2">
                  {showAdminFeatures && (
                    <>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          onEdit(podcast);
                        }}
                        className="p-2 hover:bg-secondary rounded-full transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          onDelete(podcast.id);
                        }}
                        className="p-2 hover:bg-secondary rounded-full transition-colors text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleShare(podcast);
                    }}
                    className="p-2 hover:bg-secondary rounded-full transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                {podcast.title}
              </h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                {podcast.description}
              </p>

              <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(podcast.published_at || '').toLocaleDateString('pt-PT')}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {podcast.duration}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => onPlayPause(podcast)}
                  disabled={loadingAudio && currentPodcast?.id !== podcast.id} 
                  className="flex items-center gap-2 flex-grow md:flex-grow-0"
                >
                  <PlayButton
                    podcast={podcast}
                    currentPodcast={currentPodcast}
                    isPlaying={isPlaying}
                    loadingAudio={loadingAudio}
                    onPlayPause={onPlayPause}
                    size="md"
                    variant="full"
                  />
                </button>
                <div className="hidden md:flex items-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {podcast.duration}
                  </span>
                  <span className="flex items-center gap-2">
                    <Headphones className="w-4 h-4" />
                    {podcast.plays} plays
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ))}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: showAdminFeatures ? 1 : 0 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        onClick={onCreateNew}
        className={`md:hidden fixed bottom-24 right-4 z-[999] flex flex-col items-center px-3 py-2 bg-primary text-primary-foreground rounded-lg shadow-xl hover:bg-primary/90 transition-colors text-xs font-medium w-[100px] ${!showAdminFeatures ? 'hidden' : ''}`}
        style={{
          position: 'fixed',
          bottom: '6rem',
          right: '1rem',
          transform: 'translateZ(999px)'
        }}
      >
        <Plus className="w-3.5 h-3.5 mb-0.5" />
        <span className="text-center leading-tight">Adicionar Podcast</span>
      </motion.button>
    </motion.div>
  );
};